export interface MsePlaybackChunk {
  url: string;
  startMs: number;
  endMs: number;
}

export interface MsePlaybackInfo {
  mimeType: string;
  originMs: number;
}

export interface MseAppendInfo {
  chunk: MsePlaybackChunk;
  bufferedStart: number | null;
  bufferedEnd: number | null;
  appendedSeconds: number | null;
}

interface MsePlaybackCallbacks {
  getDesiredTime(originMs: number): number | null;
  onOpen(info: MsePlaybackInfo): void;
  onAppend(info: MseAppendInfo): void;
  onError(error: Error): void;
}

export class MsePlaybackUnsupportedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MsePlaybackUnsupportedError";
  }
}

/**
 * A bounded MSE queue for recorded playback. Each physical recording file
 * starts on a keyframe; explicit timestamp offsets join those files without
 * exposing a new video URL at every recording boundary.
 */
export class RecordedMseSession {
  readonly originMs: number;

  private readonly video: HTMLVideoElement;
  private readonly callbacks: MsePlaybackCallbacks;
  private readonly knownUrls = new Set<string>();
  private readonly controllers = new Set<AbortController>();
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private objectUrl = "";
  private queueTail: Promise<void> = Promise.resolve();
  private queuedEndMs: number;
  private opened = false;
  private destroyed = false;
  private failed = false;
  private generation = 0;

  constructor(video: HTMLVideoElement, first: MsePlaybackChunk, callbacks: MsePlaybackCallbacks) {
    this.video = video;
    this.originMs = first.startMs;
    this.queuedEndMs = first.startMs;
    this.callbacks = callbacks;
    this.enqueue(first);
  }

  has(url: string): boolean {
    return this.knownUrls.has(url);
  }

  canContinueWith(chunk: MsePlaybackChunk, toleranceMs = 2_500): boolean {
    return this.has(chunk.url) || Math.abs(chunk.startMs - this.queuedEndMs) <= toleranceMs;
  }

  enqueue(chunk: MsePlaybackChunk): void {
    if (this.destroyed || this.failed || this.knownUrls.has(chunk.url)) return;
    if (this.knownUrls.size > 0 && !this.canContinueWith(chunk)) {
      this.fail(new Error("MSE playback chunk is not contiguous"));
      return;
    }
    this.knownUrls.add(chunk.url);
    this.queuedEndMs = chunk.endMs;
    this.queueTail = this.queueTail
      .then(() => this.fetchAndAppend(chunk))
      .catch((error: unknown) => this.fail(asError(error)));
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    // Invalidate every continuation before aborting the fetch/SourceBuffer.
    // Browser promises can settle after React has already detached MediaSource.
    this.generation += 1;
    for (const controller of this.controllers) controller.abort();
    this.controllers.clear();
    try {
      if (this.sourceBuffer?.updating) this.sourceBuffer.abort();
    } catch {
      // The MediaSource can already be detached while React is unmounting.
    }
    this.sourceBuffer = null;
    this.mediaSource = null;
    this.video.dataset.nvrReleasing = "true";
    this.video.pause();
    this.video.removeAttribute("src");
    this.video.load();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = "";
  }

  private async fetchAndAppend(chunk: MsePlaybackChunk): Promise<void> {
    if (this.destroyed) return;
    const generation = this.generation;
    const controller = new AbortController();
    this.controllers.add(controller);
    try {
      const response = await fetch(chunk.url, { credentials: "same-origin", signal: controller.signal });
      if (response.status === 401) window.dispatchEvent(new Event("nvr:unauthorized"));
      if (!response.ok) throw new Error(`MSE playback request failed (${response.status})`);
      if (!this.isActive(generation)) return;
      await this.appendResponse(response, chunk, generation);
    } finally {
      this.controllers.delete(controller);
    }
  }

  private async appendResponse(response: Response, chunk: MsePlaybackChunk, generation: number): Promise<void> {
    const planner = new Fmp4AppendPlanner();
    let receivedBytes = 0;
    let appended = false;
    let positioned = false;
    const appendPlanned = async (batches: Uint8Array[]) => {
      for (const batch of batches) {
        if (!this.isActive(generation)) return;
        if (!this.opened) await this.open(batch, generation);
        if (!this.isActive(generation)) return;
        if (!positioned) {
          this.positionChunk(chunk, generation);
          positioned = true;
        }
        appended = (await this.appendBytes(batch, generation)) || appended;
      }
    };

    if (response.body) {
      const reader = response.body.getReader();
      try {
        while (this.isActive(generation)) {
          const { done, value } = await reader.read();
          if (done) break;
          if (!value?.byteLength) continue;
          receivedBytes += value.byteLength;
          await appendPlanned(planner.push(value));
        }
      } finally {
        if (!this.isActive(generation)) void reader.cancel().catch(() => undefined);
        reader.releaseLock();
      }
    } else {
      // A few embedded browsers expose fetch without ReadableStream support.
      const bytes = new Uint8Array(await response.arrayBuffer());
      receivedBytes = bytes.byteLength;
      await appendPlanned(planner.push(bytes));
    }
    if (!this.isActive(generation)) return;
    await appendPlanned(planner.finish());
    if (!receivedBytes) throw new Error("MSE playback response is empty");
    if (!appended || !this.opened) throw new Error("MSE playback response contained no appendable fMP4 data");

    const afterStart = this.safeBufferedStart(generation);
    const afterEnd = this.safeBufferedEnd(generation);
    if (!this.isActive(generation)) return;
    this.callbacks.onAppend({
      chunk,
      bufferedStart: afterStart,
      bufferedEnd: afterEnd,
      appendedSeconds: afterEnd === null
        ? null
        : Math.max(0, afterEnd - mseChunkTimestampOffset(this.originMs, chunk.startMs))
    });
    await this.trimOldBuffer(generation, false);
  }

  private async open(firstBytes: Uint8Array, generation: number): Promise<void> {
    if (!("MediaSource" in window)) throw new MsePlaybackUnsupportedError("MediaSource is unavailable");
    const detected = detectFmp4MimeType(firstBytes);
    if (!detected) throw new MsePlaybackUnsupportedError("fMP4 codec configuration was not found");
    if (!MediaSource.isTypeSupported(detected.mimeType)) {
      throw new MsePlaybackUnsupportedError(`MSE does not support ${detected.mimeType}`);
    }

    const mediaSource = new MediaSource();
    const objectUrl = URL.createObjectURL(mediaSource);
    this.mediaSource = mediaSource;
    this.objectUrl = objectUrl;
    delete this.video.dataset.nvrReleasing;
    this.video.src = objectUrl;
    await waitForMediaSourceOpen(mediaSource, 8_000);
    if (!this.isActive(generation)) return;

    const sourceBuffer = mediaSource.addSourceBuffer(detected.mimeType);
    // MediaMTX starts every physical fMP4 at timestamp zero. We apply its
    // recording clock explicitly before each file, preserving small gaps and
    // replacing overlaps instead of replaying the overlap after the old tail.
    sourceBuffer.mode = "segments";
    this.sourceBuffer = sourceBuffer;
    this.opened = true;
    this.callbacks.onOpen({ mimeType: detected.mimeType, originMs: this.originMs });
  }

  private async appendBytes(bytes: Uint8Array, generation: number): Promise<boolean> {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !this.isActive(generation)) return false;
    await this.waitForBufferBudget(generation);
    if (!this.isActive(generation)) return false;
    this.updateAppendWindow(generation);
    // Own the ArrayBuffer passed to the browser. Fetch is allowed to reuse or
    // detach its Uint8Array after this turn on some implementations.
    const owned = new Uint8Array(bytes);
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const completed = await runSourceBufferOperation(
          sourceBuffer,
          () => sourceBuffer.appendBuffer(owned),
          () => !this.isActive(generation) || this.sourceBuffer !== sourceBuffer
        );
        if (completed) this.seekToDesiredWhileLoading(generation);
        return completed;
      } catch (error) {
        if (attempt > 0 || !isSourceBufferQuotaError(error) || !this.isActive(generation)) throw error;
        // A browser may enforce one aggregate MSE quota across all camera
        // elements. Reclaim this tile's old frames and retry this fragment once.
        await this.trimOldBuffer(generation, true);
        await delay(100);
        await this.waitForBufferBudget(generation);
        this.updateAppendWindow(generation);
      }
    }
    return false;
  }

  private positionChunk(chunk: MsePlaybackChunk, generation: number): void {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !this.isActive(generation)) return;
    try {
      sourceBuffer.timestampOffset = mseChunkTimestampOffset(this.originMs, chunk.startMs);
    } catch (error) {
      if (!this.isActive(generation)) return;
      throw error;
    }
  }

  private seekToDesiredWhileLoading(generation: number): void {
    if (!this.isActive(generation)) return;
    const target = mseStartupSeekTarget(this.video, this.desiredTime());
    if (target === null) return;
    this.video.currentTime = target;
  }

  private updateAppendWindow(generation: number): void {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !this.isActive(generation)) return;
    const policy = mseBufferPolicy(this.desiredTime(), this.safeBufferedStart(generation), this.safeBufferedEnd(generation));
    try {
      sourceBuffer.appendWindowStart = policy.appendWindowStart;
    } catch (error) {
      if (!this.isActive(generation)) return;
      throw error;
    }
  }

  private async waitForBufferBudget(generation: number): Promise<void> {
    let backpressured = false;
    while (this.isActive(generation)) {
      await this.trimOldBuffer(generation, false);
      const desired = this.desiredTime();
      const bufferedEnd = this.safeBufferedEnd(generation);
      if (bufferedEnd === null) return;
      const ahead = bufferedEnd - desired;
      const limit = backpressured ? mseBufferResumeAheadSeconds : mseBufferMaximumAheadSeconds;
      if (ahead <= limit) return;
      backpressured = true;
      await delay(mseBufferPollMs);
    }
  }

  private async trimOldBuffer(generation: number, emergency: boolean): Promise<void> {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !this.isActive(generation)) return;
    let first: number;
    try {
      if (sourceBuffer.buffered.length === 0) return;
      first = sourceBuffer.buffered.start(0);
    } catch (error) {
      if (!this.isActive(generation)) return;
      throw error;
    }
    const desired = this.desiredTime();
    const retainBehind = emergency ? mseBufferEmergencyBehindSeconds : mseBufferRetainBehindSeconds;
    const removeBefore = Math.max(0, desired - retainBehind);
    const minimumReclaim = emergency ? 0.25 : mseBufferTrimStepSeconds;
    if (removeBefore <= first + minimumReclaim) return;
    await runSourceBufferOperation(
      sourceBuffer,
      () => sourceBuffer.remove(first, removeBefore),
      () => !this.isActive(generation) || this.sourceBuffer !== sourceBuffer
    );
  }

  private desiredTime(): number {
    try {
      const desired = this.callbacks.getDesiredTime(this.originMs);
      if (desired !== null && Number.isFinite(desired)) return Math.max(0, desired);
    } catch {
      // The owning component may be unmounting; currentTime is still a safe
      // local fallback until the session generation is invalidated.
    }
    return Number.isFinite(this.video.currentTime) ? Math.max(0, this.video.currentTime) : 0;
  }

  private isActive(generation: number): boolean {
    return !this.destroyed && !this.failed && this.generation === generation;
  }

  private safeBufferedStart(generation: number): number | null {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !this.isActive(generation)) return null;
    try {
      return bufferedStart(sourceBuffer);
    } catch (error) {
      if (!this.isActive(generation)) return null;
      throw error;
    }
  }

  private safeBufferedEnd(generation: number): number | null {
    const sourceBuffer = this.sourceBuffer;
    if (!sourceBuffer || !this.isActive(generation)) return null;
    try {
      return bufferedEnd(sourceBuffer);
    } catch (error) {
      if (!this.isActive(generation)) return null;
      throw error;
    }
  }

  private fail(error: Error): void {
    if (this.destroyed || this.failed || error.name === "AbortError") return;
    this.failed = true;
    this.callbacks.onError(error);
  }
}

export interface DetectedFmp4Mime {
  mimeType: string;
  videoCodec: string;
  audioCodec: string | null;
}

const mseBufferMaximumAheadSeconds = 24;
const mseBufferResumeAheadSeconds = 18;
const mseBufferRetainBehindSeconds = 8;
const mseBufferEmergencyBehindSeconds = 2;
const mseBufferTrimStepSeconds = 5;
const mseBufferPollMs = 200;

export interface MseBufferPolicy {
  appendWindowStart: number;
  removeBefore: number | null;
  shouldBackpressure: boolean;
}

interface MseSeekableMedia {
  currentTime: number;
  duration: number;
  readyState: number;
  buffered: Pick<TimeRanges, "length" | "start" | "end">;
}

/**
 * Places an MSE element on the shared clock before loadeddata can fire.
 * appendWindowStart can intentionally discard time zero, so waiting for the
 * normal ready signal before seeking would leave the element parked outside
 * every buffered range.
 */
export function mseStartupSeekTarget(media: MseSeekableMedia, desiredTime: number): number | null {
  if (media.readyState > 1 || !Number.isFinite(desiredTime)) return null;
  const desired = Math.max(0, desiredTime);
  if (Math.abs(desired - media.currentTime) <= 0.75) return null;
  if (Number.isFinite(media.duration) && desired >= media.duration) return null;
  for (let index = 0; index < media.buffered.length; index += 1) {
    if (desired >= media.buffered.start(index) && desired <= media.buffered.end(index)) return desired;
  }
  return null;
}

export function mseBufferPolicy(
  desiredTime: number,
  bufferedStart: number | null,
  bufferedEnd: number | null
): MseBufferPolicy {
  const desired = Number.isFinite(desiredTime) ? Math.max(0, desiredTime) : 0;
  const appendWindowStart = Math.max(0, desired - mseBufferRetainBehindSeconds);
  return {
    appendWindowStart,
    removeBefore: bufferedStart !== null && appendWindowStart > bufferedStart + mseBufferTrimStepSeconds
      ? appendWindowStart
      : null,
    shouldBackpressure: bufferedEnd !== null && bufferedEnd - desired > mseBufferMaximumAheadSeconds
  };
}

export function isSourceBufferQuotaError(error: unknown): boolean {
  const name = typeof error === "object" && error !== null && "name" in error
    ? String(error.name)
    : "";
  const message = error instanceof Error ? error.message : String(error);
  return name === "QuotaExceededError" || /SourceBuffer is full|quota.*exceed/i.test(message);
}

export function mseChunkTimestampOffset(originMs: number, chunkStartMs: number): number {
  return Math.max(0, (chunkStartMs - originMs) / 1_000);
}

interface CompleteMp4Box {
  type: string;
  bytes: Uint8Array;
}

/**
 * Splits an arbitrarily chunked HTTP response into complete top-level MP4
 * boxes. In particular, a multi-hundred-megabyte recording is never retained
 * as one ArrayBuffer before playback can begin.
 */
export class Fmp4StreamParser {
  private pending: Uint8Array = new Uint8Array();

  push(chunk: Uint8Array): CompleteMp4Box[] {
    if (chunk.byteLength) this.pending = concatUint8Arrays(this.pending, chunk);
    return this.extract(false);
  }

  finish(): CompleteMp4Box[] {
    const boxes = this.extract(true);
    if (this.pending.byteLength) throw new Error("fMP4 response ended inside a box");
    return boxes;
  }

  private extract(final: boolean): CompleteMp4Box[] {
    const boxes: CompleteMp4Box[] = [];
    let offset = 0;
    while (this.pending.byteLength - offset >= 8) {
      const view = new DataView(this.pending.buffer, this.pending.byteOffset + offset, this.pending.byteLength - offset);
      let size = view.getUint32(0);
      let headerSize = 8;
      if (size === 1) {
        if (this.pending.byteLength - offset < 16) break;
        const large = view.getBigUint64(8);
        if (large > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("fMP4 box is too large");
        size = Number(large);
        headerSize = 16;
      } else if (size === 0) {
        if (!final) break;
        size = this.pending.byteLength - offset;
      }
      if (size < headerSize) throw new Error("fMP4 box has an invalid size");
      if (this.pending.byteLength - offset < size) break;
      boxes.push({
        type: asciiAt(this.pending, offset + 4, 4),
        bytes: this.pending.slice(offset, offset + size)
      });
      offset += size;
    }
    if (offset) this.pending = this.pending.slice(offset);
    return boxes;
  }
}

/** Groups complete boxes into an initialization append and moof/mdat appends. */
export class Fmp4AppendPlanner {
  private readonly parser = new Fmp4StreamParser();
  private initialization: Uint8Array[] = [];
  private pendingMedia: Uint8Array[] = [];
  private fragment: Uint8Array[] | null = null;
  private initialized = false;

  push(chunk: Uint8Array): Uint8Array[] {
    return this.plan(this.parser.push(chunk));
  }

  finish(): Uint8Array[] {
    const result = this.plan(this.parser.finish());
    if (!this.initialized) throw new Error("fMP4 initialization segment is missing");
    if (this.fragment) throw new Error("fMP4 response ended before fragment media data");
    if (this.pendingMedia.length) {
      result.push(concatUint8Arrays(...this.pendingMedia));
      this.pendingMedia = [];
    }
    return result;
  }

  private plan(boxes: CompleteMp4Box[]): Uint8Array[] {
    const result: Uint8Array[] = [];
    for (const box of boxes) {
      if (!this.initialized) {
        this.initialization.push(box.bytes);
        if (box.type === "moov") {
          this.initialized = true;
          result.push(concatUint8Arrays(...this.initialization));
          this.initialization = [];
        }
        continue;
      }
      if (box.type === "moof") {
        if (this.fragment) throw new Error("fMP4 fragment is missing media data");
        this.fragment = [...this.pendingMedia, box.bytes];
        this.pendingMedia = [];
      } else if (this.fragment) {
        this.fragment.push(box.bytes);
        if (box.type === "mdat") {
          result.push(concatUint8Arrays(...this.fragment));
          this.fragment = null;
        }
      } else {
        this.pendingMedia.push(box.bytes);
      }
    }
    return result;
  }
}

/** Inspect only the MP4 initialization area, never mdat payload bytes. */
export function detectFmp4MimeType(input: ArrayBuffer | Uint8Array): DetectedFmp4Mime | null {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  const moov = topLevelBox(bytes, "moov");
  if (!moov) return null;
  const view = bytes.subarray(moov.start, moov.end);

  const avcOffset = findAscii(view, "avcC");
  const hevcOffset = findAscii(view, "hvcC");
  let videoCodec: string | null = null;
  if (avcOffset >= 0 && avcOffset + 8 <= view.length) {
    videoCodec = `avc1.${hexByte(view[avcOffset + 5]!)}${hexByte(view[avcOffset + 6]!)}${hexByte(view[avcOffset + 7]!)}`;
  } else if (hevcOffset >= 0 && hevcOffset + 17 <= view.length) {
    videoCodec = hevcCodecString(view, hevcOffset + 4);
  }
  if (!videoCodec) return null;

  let audioCodec: string | null = null;
  if (findAscii(view, "mp4a") >= 0) audioCodec = "mp4a.40.2";
  else if (findAscii(view, "Opus") >= 0) audioCodec = "opus";
  else if (findAscii(view, "ipcm") >= 0) audioCodec = "ipcm";
  else if (findAscii(view, "alaw") >= 0) audioCodec = "alaw";
  else if (findAscii(view, "ulaw") >= 0) audioCodec = "ulaw";

  const codecs = audioCodec ? `${videoCodec}, ${audioCodec}` : videoCodec;
  return { mimeType: `video/mp4; codecs="${codecs}"`, videoCodec, audioCodec };
}

interface BoxRange { start: number; end: number }

function topLevelBox(bytes: Uint8Array, wanted: string): BoxRange | null {
  const data = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 0;
  while (offset + 8 <= bytes.length) {
    let size = data.getUint32(offset);
    const type = asciiAt(bytes, offset + 4, 4);
    let headerSize = 8;
    if (size === 1) {
      if (offset + 16 > bytes.length) return null;
      const large = data.getBigUint64(offset + 8);
      if (large > BigInt(Number.MAX_SAFE_INTEGER)) return null;
      size = Number(large);
      headerSize = 16;
    } else if (size === 0) {
      size = bytes.length - offset;
    }
    if (size < headerSize || offset + size > bytes.length) return null;
    if (type === wanted) return { start: offset + headerSize, end: offset + size };
    offset += size;
  }
  return null;
}

function hevcCodecString(bytes: Uint8Array, configurationStart: number): string | null {
  if (configurationStart + 13 > bytes.length || bytes[configurationStart] !== 1) return null;
  const profileByte = bytes[configurationStart + 1]!;
  const profileSpaceIndex = profileByte >> 6;
  const profileSpace = ["", "A", "B", "C"][profileSpaceIndex] ?? "";
  const tier = (profileByte & 0x20) !== 0 ? "H" : "L";
  const profileIdc = profileByte & 0x1f;
  const compatibility = reverseBits32(
    ((bytes[configurationStart + 2]! << 24) | (bytes[configurationStart + 3]! << 16)
      | (bytes[configurationStart + 4]! << 8) | bytes[configurationStart + 5]!) >>> 0
  );
  const constraints = Array.from(bytes.subarray(configurationStart + 6, configurationStart + 12));
  while (constraints.length && constraints[constraints.length - 1] === 0) constraints.pop();
  const constraintText = constraints.length ? `.${constraints.map(hexByte).join("")}` : "";
  const level = bytes[configurationStart + 12]!;
  return `hvc1.${profileSpace}${profileIdc}.${compatibility.toString(16).toUpperCase()}.${tier}${level}${constraintText}`;
}

function reverseBits32(value: number): number {
  let result = 0;
  for (let index = 0; index < 32; index += 1) {
    result = (result << 1) | ((value >>> index) & 1);
  }
  return result >>> 0;
}

function findAscii(bytes: Uint8Array, text: string): number {
  const target = Array.from(text, (character) => character.charCodeAt(0));
  outer: for (let offset = 0; offset <= bytes.length - target.length; offset += 1) {
    for (let index = 0; index < target.length; index += 1) {
      if (bytes[offset + index] !== target[index]) continue outer;
    }
    return offset;
  }
  return -1;
}

function asciiAt(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length));
}

function hexByte(value: number): string {
  return value.toString(16).padStart(2, "0").toUpperCase();
}

function concatUint8Arrays(...parts: Uint8Array[]): Uint8Array {
  if (parts.length === 1) return parts[0]!.slice();
  const result = new Uint8Array(parts.reduce((total, part) => total + part.byteLength, 0));
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.byteLength;
  }
  return result;
}

function bufferedStart(sourceBuffer: SourceBuffer): number | null {
  return sourceBuffer.buffered.length ? sourceBuffer.buffered.start(0) : null;
}

function bufferedEnd(sourceBuffer: SourceBuffer): number | null {
  return sourceBuffer.buffered.length ? sourceBuffer.buffered.end(sourceBuffer.buffered.length - 1) : null;
}

function waitForMediaSourceOpen(mediaSource: MediaSource, timeoutMs: number): Promise<void> {
  if (mediaSource.readyState === "open") return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => finish(new Error("MediaSource open timed out")), timeoutMs);
    const onOpen = () => finish();
    const onClose = () => finish(new Error("MediaSource closed before opening"));
    const finish = (error?: Error) => {
      window.clearTimeout(timer);
      mediaSource.removeEventListener("sourceopen", onOpen);
      mediaSource.removeEventListener("sourceclose", onClose);
      if (error) reject(error); else resolve();
    };
    mediaSource.addEventListener("sourceopen", onOpen, { once: true });
    mediaSource.addEventListener("sourceclose", onClose, { once: true });
  });
}

function runSourceBufferOperation(
  sourceBuffer: SourceBuffer,
  operation: () => void,
  isCancelled: () => boolean
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const onUpdateEnd = () => finish(undefined, true);
    const onError = () => finish(new Error("SourceBuffer operation failed"));
    const onAbort = () => finish(isCancelled() ? undefined : new Error("SourceBuffer operation aborted"), false);
    const finish = (error?: Error, completed = false) => {
      sourceBuffer.removeEventListener("updateend", onUpdateEnd);
      sourceBuffer.removeEventListener("error", onError);
      sourceBuffer.removeEventListener("abort", onAbort);
      if (isCancelled()) resolve(false);
      else if (error) reject(error);
      else resolve(completed);
    };
    sourceBuffer.addEventListener("updateend", onUpdateEnd, { once: true });
    sourceBuffer.addEventListener("error", onError, { once: true });
    sourceBuffer.addEventListener("abort", onAbort, { once: true });
    try {
      operation();
    } catch (error) {
      finish(asError(error));
    }
  });
}

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}
